import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import ImageGallery from 'react-image-gallery';
import classNames from 'clsx';

import { RTL } from 'arbitrip-common/translation/const';

import roomPlaceholder from '@/img/hotel_page/room_placeholder.png';

import { IconChevron, IconClose } from './Rm3Icons';

const STEP_PREV = -1;
const STEP_NEXT = 1;
const LIGHTBOX_ICON_SIZE = 20;
const MIN_PHOTOS_FOR_NAV = 2;

// Spec P2.7 (A16, as the mobile page carousel): at most five dots, a window that slides to keep the shown photo inside.
const MAX_VISIBLE_DOTS = 5;
const DOTS_BEFORE_ACTIVE = Math.floor(MAX_VISIBLE_DOTS / 2);

function visibleDotIndexes(count, active) {
    const visible = Math.min(count, MAX_VISIBLE_DOTS);
    const start = Math.min(Math.max(active - DOTS_BEFORE_ACTIVE, 0), count - visible);
    return Array.from({ length: visible }, (_unused, offset) => start + offset);
}

/**
 * Full-screen photo viewer over the room-details modal (spec P2.7). Its own MUI `Modal`, so Esc closes only the
 * viewer. `onClose(index)` hands the photo last viewed back to the modal. Arrows are logical (prev at inline-start),
 * so RTL needs no physical swap.
 */
function Rm3Lightbox({ images, alt, startIndex, onClose }) {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.dir() === RTL;
    const galleryRef = useRef(null);
    const [index, setIndex] = useState(startIndex);
    const [failedUrls, setFailedUrls] = useState(() => new Set());
    const count = images.length;
    const items = images.map((url) => ({ original: url }));
    const showNav = count >= MIN_PHOTOS_FOR_NAV;

    const close = () => onClose(index);

    // The gallery reports a slide only after its transition; a skipped click is corrected by that later report.
    const step = (delta) => {
        const gallery = galleryRef.current;
        if (!gallery) {
            return;
        }
        const target = (gallery.getCurrentIndex() + delta + count) % count;
        setIndex(target);
        gallery.slideToIndex(target);
    };

    const markFailed = (url) => {
        setFailedUrls((previous) => (previous.has(url) ? previous : new Set(previous).add(url)));
    };

    const renderItem = (item) => (
        <img
            className="rm3-lightbox__image"
            src={failedUrls.has(item.original) ? roomPlaceholder : item.original}
            alt={alt}
            onError={() => markFailed(item.original)}
        />
    );

    return (
        <Modal open onClose={close} hideBackdrop>
            <div
                className="rm3-details rm3-lightbox"
                role="dialog"
                aria-label={t('HotelDealsRm3.PhotoViewer')}
                data-testid="rm3-lightbox"
                onClick={(event) => {
                    if (event.target === event.currentTarget) {
                        close();
                    }
                }}
            >
                <div className="rm3-lightbox__stage">
                    <ImageGallery
                        ref={galleryRef}
                        items={items}
                        startIndex={startIndex}
                        onSlide={setIndex}
                        renderItem={renderItem}
                        isRTL={isRtl}
                        infinite
                        lazyLoad
                        showNav={false}
                        showThumbnails={false}
                        showPlayButton={false}
                        showFullscreenButton={false}
                    />
                </div>
                {showNav && (
                    <>
                        <button
                            type="button"
                            className="rm3-lightbox__button rm3-lightbox__nav rm3-lightbox__nav--prev"
                            aria-label={t('HotelDealsRm3.PreviousPhoto')}
                            onClick={() => step(STEP_PREV)}
                            data-testid="rm3-lightbox-prev"
                        >
                            <IconChevron size={LIGHTBOX_ICON_SIZE} className="rm3-chevron rm3-chevron--back" />
                        </button>
                        <button
                            type="button"
                            className="rm3-lightbox__button rm3-lightbox__nav rm3-lightbox__nav--next"
                            aria-label={t('HotelDealsRm3.NextPhoto')}
                            onClick={() => step(STEP_NEXT)}
                            data-testid="rm3-lightbox-next"
                        >
                            <IconChevron size={LIGHTBOX_ICON_SIZE} className="rm3-chevron" />
                        </button>
                    </>
                )}
                <button
                    type="button"
                    className="rm3-lightbox__button rm3-lightbox__close"
                    aria-label={t('HotelDealsRm3.ClosePhotoViewer')}
                    onClick={close}
                    data-testid="rm3-lightbox-close"
                >
                    <IconClose size={LIGHTBOX_ICON_SIZE} />
                </button>
                <span className="rm3-lightbox__position" dir="ltr" data-testid="rm3-lightbox-position">
                    {`${index + 1}/${count}`}
                </span>
                {showNav && (
                    <div className="rm3-lightbox__dots" aria-hidden="true">
                        <span className="rm3-lightbox__dots-pill">
                            {visibleDotIndexes(count, index).map((dot) => (
                                <span
                                    key={dot}
                                    className={classNames('rm3-lightbox__dot', {
                                        'rm3-lightbox__dot--active': dot === index,
                                    })}
                                />
                            ))}
                        </span>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export default Rm3Lightbox;
